/**
 * useCapacitySnapshot Hook
 *
 * Fetches and aggregates capacity data for a given date range.
 * Returns a snapshot of provider capacity across the specified timeframe.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  GET_SHIFTS_FORWARD,
  GET_VISITS_FORWARD,
  GET_CALENDAR_BLOCKS_FORWARD,
} from '../queries/capacityQueries';
import { buildCapacitySnapshot } from '../engine/snapshotBuilder';
import { aggregateByRole } from '../engine/aggregators';
import { generateDateRange, formatDateKey } from '../utils/dateHelpers';
import { CapacitySnapshot, ProviderData, ShiftData, VisitData, CalendarBlockData } from '../types/capacity.types';
import { getMockCapacityData } from '../mocks/mockCapacityData';

interface UseCapacitySnapshotOptions {
  startDate: Date;
  endDate: Date;
  useMockData?: boolean; // Flag to use mock data during development
}

interface UseCapacitySnapshotResult {
  snapshot: CapacitySnapshot | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch and build capacity snapshot
 */
export function useCapacitySnapshot(
  options: UseCapacitySnapshotOptions
): UseCapacitySnapshotResult {
  const { startDate, endDate, useMockData = false } = options;
  const [snapshot, setSnapshot] = useState<CapacitySnapshot | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Format dates for GraphQL queries
  const startStr = startDate.toISOString();
  const endStr = endDate.toISOString();

  // Fetch shifts
  const {
    data: shiftsData,
    loading: shiftsLoading,
    error: shiftsError,
    refetch: refetchShifts,
  } = useQuery(GET_SHIFTS_FORWARD, {
    variables: { start: startStr, end: endStr },
    skip: useMockData,
  });

  // Fetch visits
  const {
    data: visitsData,
    loading: visitsLoading,
    error: visitsError,
    refetch: refetchVisits,
  } = useQuery(GET_VISITS_FORWARD, {
    variables: { start: startStr, end: endStr },
    skip: useMockData,
  });

  // Fetch calendar blocks
  const {
    data: blocksData,
    loading: blocksLoading,
    error: blocksError,
    refetch: refetchBlocks,
  } = useQuery(GET_CALENDAR_BLOCKS_FORWARD, {
    variables: { start: startStr, end: endStr },
    skip: useMockData,
  });

  const loading = shiftsLoading || visitsLoading || blocksLoading;

  // Refetch all data
  const refetch = () => {
    if (!useMockData) {
      refetchShifts();
      refetchVisits();
      refetchBlocks();
    }
  };

  // Build snapshot when data is available
  useEffect(() => {
    try {
      if (useMockData) {
        // Use mock data for development
        const mockData = getMockCapacityData();
        buildSnapshotFromData(
          mockData.shifts,
          mockData.visits,
          mockData.calendarBlocks,
          mockData.providers
        );
      } else if (!loading && shiftsData && visitsData && blocksData) {
        // Transform GraphQL data to our types
        const providers = extractProvidersFromShifts(shiftsData);
        const shifts = extractShifts(shiftsData);
        const visits = extractVisits(visitsData);
        const blocks = extractBlocks(blocksData);

        buildSnapshotFromData(shifts, visits, blocks, providers);
      }
    } catch (err) {
      setError(err as Error);
      console.error('Error building capacity snapshot:', err);
    }
  }, [shiftsData, visitsData, blocksData, loading, useMockData, startDate, endDate]);

  // Handle errors
  useEffect(() => {
    if (shiftsError || visitsError || blocksError) {
      const firstError = shiftsError || visitsError || blocksError;
      setError(firstError as Error);
      console.error('Error fetching capacity data:', firstError);
    }
  }, [shiftsError, visitsError, blocksError]);

  function buildSnapshotFromData(
    shifts: ShiftData[],
    visits: VisitData[],
    blocks: CalendarBlockData[],
    providers: ProviderData[]
  ) {
    // Create provider map for quick lookup
    const providerMap = new Map(providers.map((p) => [p.id, p]));

    // Generate date range
    const dateRange = generateDateRange(startDate, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));

    // Build capacity snapshot
    const providerCapacity = buildCapacitySnapshot(
      shifts,
      visits,
      blocks,
      providerMap,
      dateRange
    );

    // Aggregate by role
    const roleCapacity = aggregateByRole(providerCapacity);

    setSnapshot({
      dateRange: { start: startDate, end: endDate },
      providers: providerCapacity,
      roles: roleCapacity,
      lastUpdated: new Date(),
    });
  }

  return { snapshot, loading, error, refetch };
}

/**
 * Extract provider data from shifts query response
 */
function extractProvidersFromShifts(data: any): ProviderData[] {
  const providers: ProviderData[] = [];

  if (data?.commandIndexProviders?.providers) {
    data.commandIndexProviders.providers.forEach((p: any) => {
      providers.push({
        id: p.id,
        name: `${p.firstName} ${p.lastName}`,
        role: p.providerType?.name || 'Unknown',
      });
    });
  }

  return providers;
}

/**
 * Extract shift data from query response
 */
function extractShifts(data: any): ShiftData[] {
  const shifts: ShiftData[] = [];

  if (data?.commandIndexProviders?.providers) {
    data.commandIndexProviders.providers.forEach((provider: any) => {
      if (provider.shifts) {
        provider.shifts.forEach((shift: any) => {
          shifts.push({
            id: shift.id,
            providerId: provider.id,
            startTime: new Date(shift.startTime),
            endTime: new Date(shift.endTime),
            status: shift.status,
          });
        });
      }
    });
  }

  return shifts;
}

/**
 * Extract visit data from query response
 */
function extractVisits(data: any): VisitData[] {
  const visits: VisitData[] = [];

  if (data?.visitsByTimespan) {
    data.visitsByTimespan.forEach((visit: any) => {
      visits.push({
        id: visit.id,
        eventStatus: visit.eventStatus,
        visitResources: visit.visitResources.map((resource: any) => ({
          providerId: resource.providerId,
          startTime: new Date(resource.startTime),
          endTime: new Date(resource.endTime),
        })),
      });
    });
  }

  return visits;
}

/**
 * Extract calendar block data from query response
 */
function extractBlocks(data: any): CalendarBlockData[] {
  const blocks: CalendarBlockData[] = [];

  if (data?.calendarBlocksByTimespan) {
    data.calendarBlocksByTimespan.forEach((block: any) => {
      blocks.push({
        id: block.id,
        providerId: block.providerId,
        startTime: new Date(block.startTime),
        endTime: new Date(block.endTime),
        blockType: block.blockType,
        eventStatus: block.eventStatus,
      });
    });
  }

  return blocks;
}
